import React, { useEffect, useMemo, useState } from 'react'
import {
  Plus, Search, Layers, Folder, FolderPlus, Trash2, Pin, PinOff,
  RotateCcw, X, Check, Loader2, AlertTriangle, ChevronDown,
  PenLine, Command, Paperclip, Sparkles,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { cn } from '../utils/cn'
import { stripHtml } from '../utils/format'
import { Surface } from '../components/ui'
import useWorkspace from '../components/workspace/useWorkspace'
import Editor from '../components/workspace/Editor'
import CommandPalette from '../components/workspace/CommandPalette'

// ── Helpers ──────────────────────────────────────────────────────────────────

const RICH_TEXT_TYPES = new Set(['p', 'h1', 'h2', 'h3', 'bullet', 'numbered', 'todo', 'quote'])

function relTime(iso) {
  if (!iso) return ''
  const s = (Date.now() - new Date(iso).getTime()) / 1000
  if (s < 60) return 'just now'
  if (s < 3600) return `${Math.floor(s / 60)}m ago`
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`
  if (s < 7 * 86400) return `${Math.floor(s / 86400)}d ago`
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
}

const IS_MAC = typeof navigator !== 'undefined' && /Mac/.test(navigator.platform)
const MOD = IS_MAC ? '⌘' : 'Ctrl'

/** Save chip — only ever reports what the server has actually confirmed. */
function SaveChip({ saveState, onRetry }) {
  const { status, at, message } = saveState
  if (status === 'saving') return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-600">
      <Loader2 className="w-3 h-3 animate-spin" /> Saving…
    </span>
  )
  if (status === 'saved') return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gain-600">
      <Check className="w-3 h-3" /> Saved{at ? ` ${at.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}` : ''}
    </span>
  )
  if (status === 'dirty') return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-ink-400">
      <span className="w-1.5 h-1.5 rounded-full bg-ink-300" /> Edited
    </span>
  )
  if (status === 'error') return (
    <button onClick={onRetry} className="inline-flex items-center gap-1.5 text-xs font-semibold text-loss-600 hover:text-loss-700">
      <AlertTriangle className="w-3 h-3" /> {message || 'Save failed'} — Retry
    </button>
  )
  return null
}

// ── Page ─────────────────────────────────────────────────────────────────────

const Recommendation = () => {
  const ws = useWorkspace()
  const {
    index, collections, extraCollections, trash, view, activeId,
    note, rev, loading, noteLoading, loadError, saveState, conflict,
    setView, openNote, createNote, deleteNote,
    loadTrash, restoreNote, destroyNote, patchMeta, createCollection,
    markDirty, markStructural, flushSave, uploadAttachment,
    resolveConflictTheirs, resolveConflictMine, loadIndex,
  } = ws

  const [query, setQuery] = useState('')
  const [paletteOpen, setPaletteOpen] = useState(false)
  const [collectionMenu, setCollectionMenu] = useState(false)
  const [newCollection, setNewCollection] = useState(null)   // null | '' | typing

  // Global shortcuts
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setPaletteOpen((o) => !o) }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') { e.preventDefault(); flushSave() }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [flushSave])

  // Upload errors surface here as visible toasts
  useEffect(() => {
    const onToast = (e) => toast(e.detail, { icon: '⚠️' })
    window.addEventListener('workspace-toast', onToast)
    return () => window.removeEventListener('workspace-toast', onToast)
  }, [])

  useEffect(() => { if (view === 'trash') loadTrash() }, [view, loadTrash])

  const allCollections = useMemo(
    () => [...new Set([...collections, ...extraCollections])].sort(),
    [collections, extraCollections]
  )

  const counts = useMemo(() => {
    const c = {}
    for (const n of index) c[n.collection] = (c[n.collection] || 0) + 1
    return c
  }, [index])

  const visible = useMemo(() => {
    let list = view === 'all' ? index : index.filter((n) => n.collection === view)
    const q = query.trim().toLowerCase()
    if (q) list = list.filter((n) => (n.title || '').toLowerCase().includes(q) || (n.excerpt || '').toLowerCase().includes(q))
    return list
  }, [index, view, query])

  const pinned = visible.filter((n) => n.pinned)
  const rest = visible.filter((n) => !n.pinned)

  const words = useMemo(() => {
    if (!note) return 0
    const t = (note.blocks || [])
      .map((b) => (RICH_TEXT_TYPES.has(b.type) ? stripHtml(b.text) : (b.text || (b.table ? b.table.rows.flat().join(' ') : ''))))
      .join(' ').trim()
    return t ? t.split(/\s+/).length : 0
  }, [note, rev, saveState.status])   // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreate = async (title = '') => {
    const d = await createNote(view, title)
    return d
  }

  const handleDelete = async () => {
    if (!activeId) return
    await deleteNote(activeId)
    toast('Moved to Trash — restorable any time', { icon: '🗑️' })
  }

  // ── Sub-renders ────────────────────────────────────────────────────────────

  const railItem = (active, onClick, icon, label, count, danger = false) => (
    <button
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-[13px] font-medium transition-colors',
        active
          ? 'bg-white text-ink-900 shadow-elev-1 ring-1 ring-ink-100'
          : danger ? 'text-ink-400 hover:text-loss-600 hover:bg-loss-50/60' : 'text-ink-500 hover:text-ink-800 hover:bg-ink-100/70',
      )}
    >
      {icon}
      <span className="truncate flex-1 text-left">{label}</span>
      {count != null && <span className="text-[11px] text-ink-400 tabular-nums">{count}</span>}
    </button>
  )

  const noteItem = (n) => (
    <button
      key={n.id}
      onClick={() => openNote(n.id)}
      className={cn(
        'group w-full text-left rounded-xl px-3 py-2.5 transition-colors relative',
        n.id === activeId ? 'bg-white shadow-elev-1 ring-1 ring-ink-100' : 'hover:bg-ink-100/60',
      )}
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className={cn('text-[13px] font-semibold truncate', n.title ? 'text-ink-900' : 'text-ink-400')}>
            {n.title || 'Untitled'}
          </div>
          <div className="text-xs text-ink-400 truncate mt-0.5">{n.excerpt || 'Empty note'}</div>
          <div className="flex items-center gap-2 mt-1.5 text-[10px] text-ink-400">
            <span>{relTime(n.updatedAt)}</span>
            {view === 'all' && <span className="rounded-full bg-ink-100 px-1.5 py-px text-ink-500">{n.collection}</span>}
            {n.attachmentCount > 0 && (
              <span className="inline-flex items-center gap-0.5"><Paperclip className="w-2.5 h-2.5" />{n.attachmentCount}</span>
            )}
          </div>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); patchMeta(n, { pinned: !n.pinned }) }}
          className={cn(
            'shrink-0 p-1 rounded-md transition-all',
            n.pinned ? 'text-accent-500' : 'text-ink-300 opacity-0 group-hover:opacity-100 hover:text-ink-600',
          )}
          title={n.pinned ? 'Unpin' : 'Pin'}
        >
          {n.pinned ? <Pin className="w-3 h-3 fill-current" /> : <Pin className="w-3 h-3" />}
        </button>
      </div>
    </button>
  )

  // ── Layout ─────────────────────────────────────────────────────────────────

  return (
    <div className="h-[calc(100vh-7.5rem)] lg:h-[calc(100vh-8.5rem)] min-h-[520px]">
      <Surface className="h-full flex overflow-hidden rounded-2xl">

        {/* ── Collections rail ── */}
        <aside className="hidden md:flex w-52 shrink-0 flex-col bg-ink-50/70 border-r border-ink-100">
          <div className="px-4 pt-4 pb-3">
            <div className="kicker">Workspace</div>
            <div className="text-sm font-display font-semibold text-ink-900 mt-0.5">Notes & Thinking</div>
          </div>
          <div className="flex-1 overflow-y-auto px-2.5 pb-2 space-y-0.5">
            {railItem(view === 'all', () => setView('all'), <Layers className="w-3.5 h-3.5 shrink-0" />, 'All notes', index.length)}
            <div className="pt-3 pb-1 px-2.5 text-[10px] font-semibold text-ink-400 uppercase tracking-wider">Collections</div>
            {allCollections.map((c) =>
              railItem(view === c, () => setView(c), <Folder className="w-3.5 h-3.5 shrink-0" />, c, counts[c] || 0)
            )}
            {newCollection === null ? (
              <button
                onClick={() => setNewCollection('')}
                className="w-full flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-[13px] text-ink-400 hover:text-accent-600 hover:bg-accent-50/60 transition-colors"
              >
                <FolderPlus className="w-3.5 h-3.5" /> New collection
              </button>
            ) : (
              <form
                onSubmit={(e) => { e.preventDefault(); createCollection(newCollection); setNewCollection(null) }}
                className="px-1 py-0.5"
              >
                <input
                  autoFocus
                  value={newCollection}
                  onChange={(e) => setNewCollection(e.target.value)}
                  onBlur={() => setNewCollection(null)}
                  onKeyDown={(e) => e.key === 'Escape' && setNewCollection(null)}
                  placeholder="Collection name…"
                  className="w-full rounded-lg border border-accent-200 bg-white px-2.5 py-1.5 text-[13px] outline-none focus:ring-2 focus:ring-accent-100"
                />
              </form>
            )}
          </div>
          <div className="px-2.5 pb-3 pt-2 border-t border-ink-100">
            {railItem(view === 'trash', () => setView('trash'), <Trash2 className="w-3.5 h-3.5 shrink-0" />, 'Trash', view === 'trash' ? trash.length : null, true)}
            <div className="px-2.5 pt-2 text-[10px] leading-4 text-ink-400">
              serenNotes — v1
            </div>
          </div>
        </aside>

        {/* ── Notes list ── */}
        <section className={cn('w-full md:w-72 lg:w-80 shrink-0 flex flex-col border-r border-ink-100 bg-ink-25/60', activeId && 'hidden md:flex')}>
          <div className="p-3 space-y-2 border-b border-ink-100">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-ink-300" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={view === 'trash' ? 'Trash' : 'Filter notes…'}
                  disabled={view === 'trash'}
                  className="w-full rounded-lg bg-white border border-ink-150 pl-8 pr-2.5 py-1.5 text-[13px] outline-none focus:border-accent-300 focus:ring-2 focus:ring-accent-100 placeholder:text-ink-300 disabled:opacity-50"
                />
              </div>
              {view !== 'trash' && (
                <button
                  onClick={() => handleCreate()}
                  className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-lg bg-ink-900 text-white hover:bg-ink-800 shadow-elev-1 transition-colors"
                  title={`New note in ${view === 'all' ? 'Inbox' : view}`}
                >
                  <Plus className="w-4 h-4" />
                </button>
              )}
            </div>
            <button
              onClick={() => setPaletteOpen(true)}
              className="w-full flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-[11px] text-ink-400 hover:text-ink-600 hover:bg-ink-100/70 transition-colors"
            >
              <Command className="w-3 h-3" /> Full-text search
              <kbd className="ws-kbd ml-auto">{MOD} K</kbd>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
            {loading && (
              <div className="flex items-center justify-center py-12 text-ink-300"><Loader2 className="w-5 h-5 animate-spin" /></div>
            )}
            {loadError && !loading && (
              <div className="m-2 rounded-xl border border-loss-100 bg-loss-50 p-3 text-xs text-loss-700">
                {loadError}
                <button onClick={loadIndex} className="block mt-1.5 font-semibold hover:underline">Retry</button>
              </div>
            )}

            {view === 'trash' ? (
              trash.length === 0 ? (
                <div className="px-4 py-10 text-center text-xs text-ink-400">Trash is empty</div>
              ) : (
                trash.map((n) => (
                  <div key={n.id} className="rounded-xl px-3 py-2.5 hover:bg-ink-100/60 group">
                    <div className="text-[13px] font-semibold text-ink-600 truncate">{n.title || 'Untitled'}</div>
                    <div className="text-xs text-ink-400 truncate mt-0.5">{n.excerpt || 'Empty note'}</div>
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        onClick={async () => { await restoreNote(n.id); toast.success('Note restored') }}
                        className="inline-flex items-center gap-1 rounded-md bg-white px-2 py-1 text-[11px] font-semibold text-ink-600 ring-1 ring-ink-150 hover:text-accent-700 hover:ring-accent-200"
                      >
                        <RotateCcw className="w-3 h-3" /> Restore
                      </button>
                      <button
                        onClick={async () => {
                          if (window.confirm(`Permanently delete “${n.title || 'Untitled'}” and its attachments? This cannot be undone.`)) {
                            await destroyNote(n.id)
                          }
                        }}
                        className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold text-loss-500 hover:bg-loss-50"
                      >
                        <X className="w-3 h-3" /> Delete forever
                      </button>
                    </div>
                  </div>
                ))
              )
            ) : (
              <>
                {pinned.length > 0 && (
                  <>
                    <div className="px-3 pt-2 pb-1 text-[10px] font-semibold text-ink-400 uppercase tracking-wider">Pinned</div>
                    {pinned.map(noteItem)}
                    {rest.length > 0 && <div className="px-3 pt-3 pb-1 text-[10px] font-semibold text-ink-400 uppercase tracking-wider">Notes</div>}
                  </>
                )}
                {rest.map(noteItem)}
                {!loading && visible.length === 0 && !loadError && (
                  <div className="px-4 py-10 text-center">
                    <div className="text-xs text-ink-400">{query ? 'No notes match your filter' : 'No notes here yet'}</div>
                    {!query && (
                      <button onClick={() => handleCreate()} className="mt-2 text-xs font-semibold text-accent-600 hover:text-accent-700">
                        Create one
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        {/* ── Editor pane ── */}
        <section className={cn('flex-1 min-w-0 flex flex-col bg-white', !activeId && 'hidden md:flex')}>
          {noteLoading ? (
            <div className="flex-1 flex items-center justify-center text-ink-300"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : !note ? (
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center max-w-sm">
                <div className="mx-auto w-12 h-12 rounded-2xl bg-accent-50 flex items-center justify-center mb-4">
                  {index.length === 0 ? <Sparkles className="w-5 h-5 text-accent-500" /> : <PenLine className="w-5 h-5 text-accent-500" />}
                </div>
                <h2 className="font-display font-semibold text-lg text-ink-900">
                  {index.length === 0 ? 'Your thinking space' : 'Select a note'}
                </h2>
                <p className="text-sm text-ink-500 mt-1.5 leading-6">
                  {index.length === 0
                    ? 'Notes, checklists, code, files and live spreadsheets — saved straight to disk, versioned, and yours.'
                    : 'Pick a note from the list, or start something new.'}
                </p>
                <button
                  onClick={() => handleCreate()}
                  className="mt-5 inline-flex items-center gap-2 rounded-xl bg-ink-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-ink-800 shadow-elev-2 transition-colors"
                >
                  <Plus className="w-4 h-4" /> New note
                </button>
                <div className="mt-6 space-y-1.5 text-left text-xs text-ink-400">
                  <div className="flex items-center gap-2"><kbd className="ws-kbd">/</kbd> insert blocks — tables, to-dos, code, files</div>
                  <div className="flex items-center gap-2"><kbd className="ws-kbd">=SUM(A1:B2)</kbd> live formulas in tables</div>
                  <div className="flex items-center gap-2"><kbd className="ws-kbd">{MOD} K</kbd> search everything, instantly</div>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Editor header */}
              <div className="shrink-0 px-6 sm:px-10 pt-4 pb-2 border-b border-ink-100/70">
                <div className="flex items-center gap-3 text-xs">
                  <button onClick={() => { flushSave(); ws.closeNote() }} className="md:hidden text-ink-400 hover:text-ink-700">← Notes</button>
                  {/* Collection switcher */}
                  <div className="relative">
                    <button
                      onClick={() => setCollectionMenu((o) => !o)}
                      className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-medium text-ink-500 hover:bg-ink-50 hover:text-ink-800 transition-colors"
                    >
                      <Folder className="w-3 h-3" /> {note.collection}
                      <ChevronDown className="w-3 h-3" />
                    </button>
                    {collectionMenu && (
                      <>
                        <div className="fixed inset-0 z-30" onClick={() => setCollectionMenu(false)} />
                        <div className="absolute left-0 top-full z-40 mt-1 w-48 rounded-xl bg-white border border-ink-100 shadow-elev-3 py-1.5 animate-scale-in">
                          {allCollections.map((c) => (
                            <button
                              key={c}
                              onClick={() => {
                                setCollectionMenu(false)
                                if (c !== note.collection) { note.collection = c; markStructural() }
                              }}
                              className={cn('ws-menu-item', c === note.collection && 'text-accent-700 font-semibold')}
                            >
                              <Folder className="w-3.5 h-3.5" /> {c}
                              {c === note.collection && <Check className="w-3 h-3 ml-auto" />}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                  <button
                    onClick={() => { note.pinned = !note.pinned; markStructural() }}
                    className={cn('inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium transition-colors',
                      note.pinned ? 'text-accent-600 bg-accent-50' : 'text-ink-400 hover:bg-ink-50 hover:text-ink-700')}
                  >
                    {note.pinned ? <Pin className="w-3 h-3 fill-current" /> : <PinOff className="w-3 h-3" />}
                    {note.pinned ? 'Pinned' : 'Pin'}
                  </button>
                  <div className="flex-1" />
                  <SaveChip saveState={saveState} onRetry={() => { markDirty(); flushSave() }} />
                  <button
                    onClick={handleDelete}
                    className="text-ink-300 hover:text-loss-600 transition-colors p-1 rounded-md hover:bg-loss-50"
                    title="Move to Trash"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>

                {/* Conflict banner — honest resolution, never a silent overwrite */}
                {conflict && (
                  <div className="mt-2 flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-xs text-amber-800">
                    <AlertTriangle className="w-4 h-4 shrink-0" />
                    <span className="flex-1">This note was changed elsewhere (v{conflict.version}). Choose which version to keep.</span>
                    <button onClick={resolveConflictTheirs} className="font-semibold rounded-md bg-white px-2.5 py-1 ring-1 ring-amber-200 hover:bg-amber-100">Load latest</button>
                    <button onClick={resolveConflictMine} className="font-semibold rounded-md bg-amber-600 text-white px-2.5 py-1 hover:bg-amber-700">Keep mine</button>
                  </div>
                )}
              </div>

              {/* Title + blocks */}
              <div className="flex-1 overflow-y-auto">
                <div className="max-w-3xl mx-auto px-8 sm:px-12 pt-6">
                  <input
                    key={note.id}
                    defaultValue={note.title}
                    autoFocus={!note.title}
                    placeholder="Untitled"
                    spellCheck={false}
                    onChange={(e) => { note.title = e.target.value; markDirty() }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === 'ArrowDown') {
                        e.preventDefault()
                        document.querySelector('.ws-block')?.focus()
                      }
                    }}
                    className="w-full bg-transparent outline-none font-display font-semibold text-[28px] leading-tight tracking-tight text-ink-900 placeholder:text-ink-200 mb-3"
                  />
                  <Editor
                    note={note}
                    rev={rev}
                    onText={markDirty}
                    onStructure={markStructural}
                    uploadAttachment={uploadAttachment}
                  />
                </div>
              </div>

              {/* Footer — quiet facts */}
              <div className="shrink-0 px-6 sm:px-10 py-2 border-t border-ink-100/70 flex items-center gap-3 text-[10px] text-ink-400">
                <span className="tabular-nums">{words.toLocaleString('en-IN')} words</span>
                <span>·</span>
                <span className="tabular-nums">{note.blocks.length} blocks</span>
                {(note.attachments || []).length > 0 && (
                  <>
                    <span>·</span>
                    <span className="tabular-nums inline-flex items-center gap-1"><Paperclip className="w-2.5 h-2.5" />{note.attachments.length} files</span>
                  </>
                )}
                <span className="flex-1" />
                <span>v{note.version}</span>
              </div>
            </>
          )}
        </section>
      </Surface>

      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        index={index}
        onOpenNote={openNote}
        onCreateNote={(title) => handleCreate(title)}
      />
    </div>
  )
}

export default Recommendation
